import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  Logout as LogoutIcon,
  UploadFile as FileIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import { authApi } from "@/api";
import { DataFormat, ImportMode, DATA_FORMAT_OPTIONS } from "@/constants";
import { EMPTY_CELL_PLACEHOLDER } from "@/constants/display";
import { useSettingsDataExchange } from "@/hooks";

export default function Settings() {
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const { notify } = useNotification();
  const {
    fileInputRef,
    exportFormat,
    setExportFormat,
    importFormat,
    setImportFormat,
    importMode,
    setImportMode,
    selectedFile,
    exporting,
    importing,
    handleExport,
    handleImport,
    handleFileSelect,
    clearSelectedFile,
    acceptExtensions,
  } = useSettingsDataExchange();

  const handleDeleteAccount = async () => {
    if (!window.confirm(t("settings.deleteAccountConfirm"))) return;
    try {
      await authApi.deleteAccount();
      notify(t("settings.accountDeleted"), "success");
      await logout();
    } catch {
      notify(t("common.error"), "error");
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          mb: 3,
        }}
      >
        {t("settings.title")}
      </Typography>
      <Stack spacing={3}>
        <Card variant="outlined">
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
              }}
            >
              {t("settings.account")}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              sx={{
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography variant="body1">
                {user?.email ?? EMPTY_CELL_PLACEHOLDER}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteAccount}
                >
                  {t("settings.deleteAccount")}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<LogoutIcon />}
                  onClick={() => logout()}
                >
                  {t("auth.logout")}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
              }}
            >
              {t("settings.dataManagement")}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  <ExportIcon
                    fontSize="small"
                    sx={{ verticalAlign: "middle", mr: 1 }}
                  />
                  {t("settings.exportAll")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mb: 2,
                  }}
                >
                  {t("settings.exportDesc")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>{t("settings.format")}</InputLabel>
                    <Select
                      value={exportFormat}
                      label={t("settings.format")}
                      onChange={(e) =>
                        setExportFormat(e.target.value as DataFormat)
                      }
                    >
                      {DATA_FORMAT_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={
                      exporting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <ExportIcon />
                      )
                    }
                    onClick={handleExport}
                    disabled={exporting}
                  >
                    {exporting
                      ? t("settings.exporting")
                      : t("settings.exportAll")}
                  </Button>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  <ImportIcon
                    fontSize="small"
                    sx={{ verticalAlign: "middle", mr: 1 }}
                  />
                  {t("settings.importAll")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mb: 2,
                  }}
                >
                  {t("settings.importDesc")}
                </Typography>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      alignItems: "center",
                    }}
                  >
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>{t("settings.format")}</InputLabel>
                      <Select
                        value={importFormat}
                        label={t("settings.format")}
                        onChange={(e) =>
                          setImportFormat(e.target.value as DataFormat)
                        }
                      >
                        {DATA_FORMAT_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>{t("settings.importMode")}</InputLabel>
                      <Select
                        value={importMode}
                        label={t("settings.importMode")}
                        onChange={(e) =>
                          setImportMode(e.target.value as ImportMode)
                        }
                      >
                        <MenuItem value={ImportMode.Merge}>
                          {t("settings.merge")}
                        </MenuItem>
                        <MenuItem value={ImportMode.Replace}>
                          {t("settings.replace")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    {importMode === ImportMode.Merge
                      ? t("settings.mergeDesc")
                      : t("settings.replaceDesc")}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<FileIcon />}
                    >
                      {t("settings.selectFile")}
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept={acceptExtensions}
                        onChange={handleFileSelect}
                      />
                    </Button>
                    {selectedFile ? (
                      <Chip
                        label={selectedFile.name}
                        onDelete={clearSelectedFile}
                        size="small"
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        {t("settings.noFile")}
                      </Typography>
                    )}
                  </Stack>
                  <Box>
                    <Button
                      variant="contained"
                      startIcon={
                        importing ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <ImportIcon />
                        )
                      }
                      onClick={handleImport}
                      disabled={importing || !selectedFile}
                    >
                      {importing
                        ? t("settings.importing")
                        : t("settings.importAll")}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
